import { useState, useMemo } from 'react'
import { Trash2, X, ChevronDown } from 'lucide-react'
import type { WorkflowStatus } from '../api'
import { PRIORITY_OPTIONS as PRIORITIES } from '../boardUtils'

export interface ListBulkToolbarProps {
  selectedCount: number
  totalCount: number
  statuses: WorkflowStatus[]
  onSelectAll: () => void
  onClear: () => void
  onBulkStatus: (statusId: string) => void
  onBulkPriority: (priority: string) => void
  onBulkDelete: () => void
  busy?: boolean
}

export function ListBulkToolbar({
  selectedCount,
  totalCount,
  statuses,
  onSelectAll,
  onClear,
  onBulkStatus,
  onBulkPriority,
  onBulkDelete,
  busy = false,
}: ListBulkToolbarProps) {
  const [statusMenuOpen, setStatusMenuOpen] = useState(false)
  const [priorityMenuOpen, setPriorityMenuOpen] = useState(false)

  const uniqueStatuses = useMemo(() => {
    const seen = new Set<string>()
    return statuses.filter((st) => {
      const key = st.name.toLowerCase().trim()
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
  }, [statuses])

  if (selectedCount === 0) return null

  return (
    <div className="fixed bottom-6 left-1/2 z-40 -translate-x-1/2 flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-2.5 shadow-2xl dark:border-slate-700 dark:bg-slate-900 animate-in fade-in slide-in-from-bottom-4 duration-150">
      <div className="flex items-center gap-2 pr-2 border-r border-gray-200 dark:border-slate-700">
        <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-blue-600 px-1.5 text-xs font-bold text-white">
          {selectedCount}
        </span>
        <span className="text-xs font-medium text-gray-700 dark:text-slate-300">
          selected
        </span>
        {selectedCount < totalCount && (
          <button
            onClick={onSelectAll}
            className="ml-1 text-xs text-blue-600 hover:underline dark:text-blue-400"
          >
            Select all {totalCount}
          </button>
        )}
      </div>

      <div className="relative">
        <button
          onClick={() => {
            setStatusMenuOpen(!statusMenuOpen)
            setPriorityMenuOpen(false)
          }}
          disabled={busy}
          className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
        >
          <span>Change status</span>
          <ChevronDown className="h-3 w-3" />
        </button>

        {statusMenuOpen && (
          <div className="absolute bottom-full left-0 mb-1 w-44 rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-800">
            {uniqueStatuses.map((s) => (
              <button
                key={s.id}
                onClick={() => {
                  onBulkStatus(s.id)
                  setStatusMenuOpen(false)
                }}
                className="w-full px-3 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-100 dark:text-slate-200 dark:hover:bg-slate-700"
              >
                {s.name}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="relative">
        <button
          onClick={() => {
            setPriorityMenuOpen(!priorityMenuOpen)
            setStatusMenuOpen(false)
          }}
          disabled={busy}
          className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
        >
          <span>Change priority</span>
          <ChevronDown className="h-3 w-3" />
        </button>

        {priorityMenuOpen && (
          <div className="absolute bottom-full left-0 mb-1 w-40 rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-800">
            {PRIORITIES.map((p) => (
              <button
                key={p.value}
                onClick={() => {
                  onBulkPriority(p.value)
                  setPriorityMenuOpen(false)
                }}
                className="w-full px-3 py-1.5 text-left text-xs capitalize text-gray-700 hover:bg-gray-100 dark:text-slate-200 dark:hover:bg-slate-700"
              >
                {p.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <button
        onClick={onBulkDelete}
        disabled={busy}
        className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40"
      >
        <Trash2 className="h-3.5 w-3.5" />
        <span>Delete</span>
      </button>

      <button
        onClick={onClear}
        className="ml-1 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-slate-800"
        title="Deselect all"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}
